// @flow
import { logMessage } from '../sentry';
import { isWeb } from '../environment-utils';
import { getDisplayTrackUrl } from '../TrackAnalyticsHelper';
import envVars from '../config';
import {
  EVENT_OMID_DISPLAY_TRACKING,
  EVENT_OMID_SDK_ACTIVATED,
  setOmidTrackingEvent
} from './omid-tracking';
import { injectTags } from '../inject-tags';
import { partnerInfos } from './partner-infos';

const omidSessionClient: OmidSessionClient | undefined = (window as any)
  .OmidSessionClient;
const omSdk: OMSDK | undefined = omidSessionClient && omidSessionClient.default;

let adEvents: any;
let mediaEvents: any;
let adSession: any;
let adSessionContext: any;
let shouldSkipNextEvents = false;
let impressionHasBeenSent = false;
let isCreativeLoaded = false;
let isSessionStarted = false;
let loadedVastProperties: any = null;
let pendingImpression = false;
let impressionDelay = 0;

const mediaEventNames = [
  'video',
  'loaded',
  'start',
  'firstQuartile',
  'midpoint',
  'thirdQuartile',
  'complete',
  'pause',
  'resume',
  'bufferStart',
  'bufferFinish',
  'skipped',
  'volumeChange',
  'playerStateChange',
  'adUserInteraction'
];

const IMPRESSION_TYPE = 'beginToRender';
const IMPRESSION_TYPE_MWEB = 'beginToRender';
const CREATIVE_DISPLAY_TYPE = 'htmlDisplay';
const CREATIVE_VIDEO_TYPE = 'video';

/**
 * Create a new AdSession
 */
function getAdSession(): any {
  // Omid SDK not loaded in the page
  if (!omSdk) {
    throw new Error('omSdk not found.');
  }

  if (!adSession) {
    throw new Error('adSession not found.');
  }

  return adSession;
}

export function isOMIDSDKActivated(): boolean {
  if (!window.OmidSessionClient || !window.OmidSessionClient.default) {
    return false;
  }
  const {
    AdSession,
    Context,
    Partner,
    VerificationScriptResource
  }: OMSDK = window.OmidSessionClient.default;

  if (!AdSession || !Context || !Partner || !VerificationScriptResource) {
    return false;
  }
  return true;
}

function createAdSession(
  javascriptResources: VastProperties[] = [],
  isVideo: boolean = false,
  htmlElement: HTMLElement,
  videoPlayer: HTMLVideoElement | null = null
): any {
  // Omid SDK not loaded in the page
  if (!omSdk) {
    throw new Error('omSdk not found.');
  }

  if (adSession) {
    throw new Error('omSdk adSession already created.');
  }

  const {
    AdSession,
    Context,
    Partner,
    VerificationScriptResource,
    VastProperties
  }: OMSDK = omSdk;

  if (
    !AdSession ||
    !Context ||
    !Partner ||
    !VerificationScriptResource ||
    !VastProperties
  ) {
    throw new Error('omSdk does not have a proper implementation.');
  }

  const partner = new Partner(
    partnerInfos.name,
    (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) || partnerInfos.version
  );

  const scriptResources = javascriptResources.map(
    (jsResource: any) =>
      new VerificationScriptResource(
        jsResource.resourceUrl,
        jsResource.vendorKey,
        jsResource.verificationParameters,
        'full' // accessMode
      )
  );

  adSessionContext = new Context(partner, scriptResources);
  adSessionContext.setSlotElement(htmlElement);

  if (isWeb()) {
    if (videoPlayer) {
      // In mWeb environment, we need to set the video element before the AdSession
      // Without this line we cannot get the viewability metadatas in the impression event
      adSessionContext.setVideoElement(videoPlayer);
    }

    adSessionContext.underEvaluation = true;
    adSessionContext.setServiceWindow(window);
  }

  adSession = new AdSession(adSessionContext);

  if (videoPlayer && !isWeb()) {
    // In In-app environment, we need to set the video element after the AdSession
    // This prevent the OMSDK to throw an error in the console
    adSessionContext.setVideoElement(videoPlayer);
  }

  if (!adSession.isSupported()) {
    throw new Error('omSdk adSession is not supported.');
  }

  const creativeType = isVideo ? CREATIVE_VIDEO_TYPE : CREATIVE_DISPLAY_TYPE;

  if (isWeb()) {
    adSession.setCreativeType(creativeType);
    adSession.setImpressionType(IMPRESSION_TYPE_MWEB);
  }

  try {
    adSession.registerSessionObserver((event: any) => {
      if (event.type === 'sessionStart') {
        isSessionStarted = true;

        if (event.data.creativeType === 'definedByJavaScript') {
          adSession.setCreativeType(creativeType);
        }

        if (event.data.impressionType === 'definedByJavaScript') {
          adSession.setImpressionType(IMPRESSION_TYPE);
        }

        // Dispatch any impression that was queued before the session started
        // Must happen before loaded() which could trigger side effects
        if (pendingImpression) {
          pendingImpression = false;
          sendOmidImpression();
        }

        if (isCreativeLoaded) {
          adEvents.loaded(loadedVastProperties);
        }
      }
    });
  } catch (error) {
    logMessage(
      'Omid session register errored.',
      undefined,
      (error as any).message
    );
  }

  if (isWeb()) {
    adSession.start();
  }

  return adSession;
}

/**
 * Initialize OMID adSession with a new Partner and Context
 */
export function initOmidAdEvents(
  htmlElement: HTMLElement,
  omidTrackers: string,
  javascriptResources: VastProperties[] = [],
  enableOmidTag: boolean = true,
  isVideo: boolean = false,
  videoPlayer: HTMLVideoElement | null = null,
  enableOmidViewabilityTracks: boolean = false
): void {
  setOmidTrackingEvent(EVENT_OMID_SDK_ACTIVATED, Boolean(omSdk));
  setOmidTrackingEvent(EVENT_OMID_DISPLAY_TRACKING, omidTrackers !== '');

  if (!omSdk) {
    return;
  }

  if (adEvents) {
    return;
  }

  if (!omSdk.AdEvents) {
    logMessage('omSdk does not have AdEvents field.');
    return;
  }

  try {
    injectOmidTrackers(omidTrackers);

    if (enableOmidTag) {
      javascriptResources.push({
        resourceUrl: envVars.INHOUSE_OMID_URL,
        vendorKey: 'ogury',
        verificationParameters: enableOmidViewabilityTracks
          ? { trackUrl: getDisplayTrackUrl('[event]') }
          : {},
        isSkippable: false,
        skipOffset: 0,
        isAutoPlay: false,
        position: ''
      });
    }

    const omAdSession = createAdSession(
      javascriptResources,
      isVideo,
      htmlElement,
      videoPlayer
    );
    adEvents = new omSdk.AdEvents(omAdSession);
  } catch (error) {
    logMessage('init Omid AdEvents failed.', undefined, (error as any).message);
  }
}

export function setVideoElementToContext(
  videoHtmlElement: HTMLVideoElement
): void {
  if (!omSdk || !adSessionContext) {
    return;
  }

  try {
    adSessionContext.setVideoElement(videoHtmlElement);
  } catch (error) {
    logMessage(
      'setVideoElementToContext failed.',
      undefined,
      (error as any).message
    );
  }
}

/**
 * Initialize OMID Video
 */
export function initOmidMediaEvents(): void {
  if (!omSdk) {
    return;
  }

  if (mediaEvents) {
    return;
  }

  if (!omSdk.MediaEvents) {
    logMessage('omSdk does not have MediaEvents field.');
    return;
  }

  try {
    const adSessionRef = getAdSession();
    mediaEvents = new omSdk.MediaEvents(adSessionRef);
  } catch (error) {
    logMessage(
      'init Omid MediaEvents failed',
      undefined,
      (error as any).message
    );
  }
}

/**
 * Send a video event to OMID SDK service
 */
export function sendOmidMediaEvent(eventType: string, ...options: any[]): void {
  if (shouldSkipNextEvents) {
    return;
  }

  if (!mediaEvents) {
    return;
  }

  if (mediaEventNames.indexOf(eventType) === -1) {
    return;
  }

  if (typeof (mediaEvents as any)[eventType] !== 'function') {
    logMessage('MediaEvents is not a function', {
      eventType
    });
    return;
  }

  try {
    // When the video has been clicked, no events should be fired.
    if (eventType === 'skipped') {
      sendOmidSessionFinish();
      shouldSkipNextEvents = true;
    }

    (mediaEvents as any)[eventType](...options);
  } catch (error) {
    logMessage(
      `send Omid MediaEvent : unable to call the method ${eventType}.`,
      {
        eventType
      },
      (error as any).message
    );
  }
}

/**
 * Send an event to OMID SDK service that an impression occurred
 */
export function sendOmidImpression(): void {
  if (!adEvents) {
    return;
  }

  // Queue if session not started yet
  if (!isSessionStarted) {
    pendingImpression = true;
    return;
  }

  const sendImpression = () => {
    if (impressionHasBeenSent) {
      return;
    }

    try {
      adEvents.impressionOccurred();
      impressionHasBeenSent = true;
    } catch (error) {
      if (Math.random() > 0.8) {
        logMessage(
          'Omid Impression is not sent.',
          undefined,
          JSON.stringify(error)
        );
      }
    }
  };

  if (impressionDelay === 0) {
    sendImpression();
  } else {
    setTimeout(sendImpression, impressionDelay);
  }
}

/**
 * Send an event to OMID SDK service that a loaded event occurred
 */
export function sendOmidLoaded(vastProperties: any = null): void {
  if (!adEvents) {
    return;
  }

  try {
    if (vastProperties) {
      const { isSkippable, skipOffset, isAutoPlay, position } = vastProperties;

      loadedVastProperties = new omSdk!.VastProperties(
        isSkippable,
        skipOffset,
        isAutoPlay,
        position
      );
    }

    if (isSessionStarted && !isCreativeLoaded) {
      adEvents.loaded(loadedVastProperties);
    }
  } catch (error) {
    logMessage('Omid Loaded is not sent.', undefined, (error as any).message);
  }

  isCreativeLoaded = true;
}

/**
 * Only for MWEB
 * Send an event to OMID SDK service that finish the session
 */
export function sendOmidSessionFinish(): void {
  if (isWeb()) {
    if (!adSession) {
      return;
    }

    try {
      adSession.finish();
    } catch (error) {
      logMessage(
        'Omid session finish is not sent.',
        undefined,
        (error as any).message
      );
    }
  }
}

function injectOmidTrackers(omidTrackers: string): void {
  let trackers: string[] = [];

  // Inject 3rd party's trackers
  if (omidTrackers) {
    trackers.push(omidTrackers);
  }

  const injectInto = 'omid-tags';
  const injectIntoSelector = `#${injectInto}`;
  const container = document.createElement('div');
  container.id = injectInto;
  document.body.appendChild(container);

  injectTags(
    trackers.join(''),
    injectIntoSelector,
    injectIntoSelector,
    'body',
    {
      overloadDocumentWrite: false,
      enableSentryLog: false,
      allowInjectIntoBody: true
    }
  );
}

export function setOmidImpressionDelay(omidImpressionDelay: number = 0): void {
  impressionDelay = omidImpressionDelay;
}
